import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AppConstants } from '../../constants/constants';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { PcrEmpiConstants } from 'src/constants/empi_constants';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from '../../environments/environment';
import { fhirR4 } from '@smile-cdr/fhirts';
import { map } from 'rxjs/operators';
import { parse } from 'yamljs';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  constructor(
    private httpClient: HttpClient,
    private oauthService: OAuthService,
    private storageService: StorageService
  ) { }

  updateFHIRResource(resource) {
    return this.httpClient
      .put(environment.resourceProviderURI + '/' + resource.resourceType + '/' + resource.id, resource, { headers: this.getHeaders() })
      .toPromise();
  }

  postFHIRResource(resource) {
    return this.httpClient
      .post(environment.resourceProviderURI + '/' + resource.resourceType + '/', resource, { headers: this.getHeaders() })
      .toPromise();
  }

  putFHIRResource(resource) {
    return this.httpClient
      .put(environment.resourceProviderURI + '/' + resource.resourceType + '/' + resource.id, resource, { headers: this.getHeaders() })
      .toPromise();
  }

  matchPatient(data) {
    return this.httpClient
      .post(environment.resourceProviderURI + '/Patient/$match', data, { headers: this.getHeaders() })
      .toPromise();
  }

  getAllResourcesOfSpecifiedType(fhirResourceType: string, addNoCacheControlHeaders?: boolean) {
    const query = fhirResourceType;
    return this.executeGETQueryWithParams(query, addNoCacheControlHeaders);
  }

  getResourceWithTypeAndId(resourceType, resourceId) {
    const query = resourceType + '/' + resourceId;
    return this.executeGETQueryWithParams(query);
  }

  getReferenceDataWithName(name) {
    const query = 'CodeSystem'  + '?name=' + name;
    return this.executeGETQueryWithParams(query);
  }

  getEMPIPatientByEid(eid) {
    const query = 'Patient/' + eid;
    return this.executeGETQueryWithParamsEMPI(query);
  }

  getQuestionnaireByIdentifier(identifier: string) {
    const query = 'Questionnaire?identifier=' + identifier;
    return this.executeGETQueryWithParams(query);
  }

  getAllLocationsRelatedToOrganization(organizationId) {
    const query = AppConstants.location + '?organization=' + organizationId;
    return this.executeGETQueryWithParams(query);
  }

  searchForPatientById(patientId) {
    const query = 'Patient?_id=' + patientId;
    return this.executeGETQueryWithParams(query);
  }

  searchForPatientByEId(eid) {
    const query = 'Patient?identifier=' + PcrEmpiConstants.enterpriseIdNamingSystem + '|' + eid;
    return this.executeGETQueryWithParams(query);
  }

  searchLocalPatientByIdentifier(identifer: fhirR4.Identifier, noCacheHeader?: boolean) {
    const query = 'Patient?_sort=_id&identifier=' + identifer.system + '|' + identifer.value;
    return this.executeGETQueryWithParams(query, noCacheHeader);
  }

  searhEMPIPatientByIdentifier(identifer: fhirR4.Identifier) {
    const query = 'Patient/$identifier-search-operation?identifier=' +
    identifer.system + '|' + identifer.value + '&organization=' + this.storageService.getStorageItem('userOrgIdentifier');
    return this.executeGETQueryWithParamsEMPI(query);
  }

  searchPatientByClinicalEntryId(entryId: string, siteIds: string[]) {
    let query = `ServiceRequest?_include=ServiceRequest:subject`;
    if (siteIds?.length > 0) {
      query = query + `&performer:Organization=${siteIds.join(',')}`;
    }
    // #3571 If an Entry has an identifier, the identifier should be displayed. This is for migrated entries
    const bundle: fhirR4.Bundle = new fhirR4.Bundle();
    bundle.resourceType = 'Bundle';
    bundle.type = 'transaction';

    const identifierEntry: fhirR4.BundleEntry = new fhirR4.BundleEntry();
    const identifierEntryRequest: fhirR4.BundleRequest = new fhirR4.BundleRequest();
    identifierEntryRequest.method = 'GET';
    identifierEntryRequest.url = query + '&identifier=' + AppConstants.ENTRY_IDENTIFIER_SYSTEM + '|' + entryId;
    identifierEntry.request = identifierEntryRequest;

    const logicalIDEntry: fhirR4.BundleEntry = new fhirR4.BundleEntry();
    const logicalIDEntryRequest: fhirR4.BundleRequest = new fhirR4.BundleRequest();
    logicalIDEntryRequest.method = 'GET';
    logicalIDEntryRequest.url = query + '&_id=' + entryId;
    logicalIDEntry.request = logicalIDEntryRequest;

    bundle.entry = [identifierEntry, logicalIDEntry];

    return this.postBundleResource(bundle);
  }

  /*getCardiacSites() {
    const query = `Organization?identifier= ${AppConstants.mrnIssuerSystem}|`;
    return this.executeGETQueryWithParams(query);
  }
  */

  deleteResources(queryParams) {
    return this.httpClient.delete(environment.resourceProviderURI + '/' + queryParams,
      { headers: this.getHeaders() }).toPromise();
  }


  getInFieldValidation() {
    return this.httpClient.get('./assets/validations/in-field-validations.json').toPromise();
  }

  getInFieldValidationForUserPreference() {
    return this.httpClient.get('./assets/validations/in-field-validations-user-preference.json').toPromise();
  }
  getCrossFieldValidation() {
    return this.httpClient.get('./assets/validations/cross-field-validations.json').toPromise();
  }

  getDropDownRestrictions() {
    return this.httpClient.get('./assets/validations/dropdown-option-restrictions.json').toPromise();
  }

  findPatientsUsingEmpiMatchOperation(parameters: fhirR4.Parameters) {
    const headers = this.getHeaders();
    return this.httpClient.post(environment.pcrEmpiURI + '/Patient/$match-operation', parameters, { headers }).toPromise();
  }

  findPatientsUsingEmpiSearch(parameters: fhirR4.Parameters) {
    const headers = this.getHeaders();
    return this.httpClient.post(environment.pcrEmpiURI + '/Patient/$match-operation?organization=' +
    this.storageService.getStorageItem('userOrgIdentifier'), parameters, { headers }).toPromise();
  }

  executeGETQueryWithParams(queryParams: string, addNoCacheControlHeaders?: boolean) {
    let headers: HttpHeaders;
    if (addNoCacheControlHeaders) {
      headers = this.getNoCacheHeaders();
    } else {
      headers = this.getHeaders();
    }
    return this.httpClient.get(environment.resourceProviderURI + '/' + queryParams,
      { headers }).toPromise();
  }

  executeGETQueryWithParamsEMPI(queryParams: string, addNoCacheControlHeaders?: boolean) {
    let headers: HttpHeaders;
    if (addNoCacheControlHeaders) {
      headers = this.getNoCacheHeaders();
    } else {
      headers = this.getHeaders();
    }
    return this.httpClient.get(environment.pcrEmpiURI + '/' + queryParams,
      { headers }).toPromise();
  }

  getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.oauthService.getAccessToken()
    });
    return headers;
  }

  getBundleHeaders() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.oauthService.getAccessToken(),
      'Prefer': 'return=representation',
      'Cache-Control': 'no-cache'
    });
    return headers;
  }

  getNoCacheHeaders() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.oauthService.getAccessToken(),
      'Cache-Control': 'no-cache'
    });
    return headers;
  }

  getDropDownAuthorityIssuers() {
    return this.httpClient.get('./assets/config/authority-issuing-card.json').toPromise();
  }

  getSiteServiceCodes() {
    const query  = `HealthcareService?_count=${AppConstants.QUERY_STRING_PARAM_COUNT_VALUE}&service-category=${AppConstants.serviceCategorySiteService}`;
    return this.executeGETQueryWithParams(query);
  }

  // getMrnIssuers() {
  //   return this.httpClient.get('./assets/config/mrn-issuer.json').toPromise();

  // }

  sendDcisPatientMatchNotification(subject: string, bodayMessage: string, logOnly: boolean) {
    const headers = this.getHeaders();
    const email =   {
      'to': environment.sendEmailTo,
      'subject': subject,
      'body': bodayMessage,
      'isHtml': true,
      'logEmailOnly': logOnly
    };
    return this.httpClient.post(environment.sendEmailApiUri, email, {headers}).toPromise();
  }

  fetchEntryClinicalResources(entryId: string) {
    const bundle: fhirR4.Bundle = new fhirR4.Bundle();
    bundle.resourceType = 'Bundle';
    bundle.type = 'transaction';
    bundle.entry = [];

    const serviceRequestQuery = 'ServiceRequest?_include=*&_id=' + entryId;
    const serviceRequestEntry: fhirR4.BundleEntry = new fhirR4.BundleEntry();
    const serviceRequestEntryRequest: fhirR4.BundleRequest = new fhirR4.BundleRequest();
    serviceRequestEntryRequest.method = 'GET';
    serviceRequestEntryRequest.url = serviceRequestQuery;
    serviceRequestEntry.request = serviceRequestEntryRequest;
    bundle.entry.push(serviceRequestEntry);

    const appointmentQuery = 'Appointment?_include=Appointment:practitioner&based-on._id=' + entryId;
    const appointmentEntry: fhirR4.BundleEntry = new fhirR4.BundleEntry();
    const appointmentEntryRequest: fhirR4.BundleRequest = new fhirR4.BundleRequest();
    appointmentEntryRequest.method = 'GET';
    appointmentEntryRequest.url = appointmentQuery;
    appointmentEntry.request = appointmentEntryRequest;
    bundle.entry.push(appointmentEntry);

    const diagnosticReportAndRelatedResourcesQuery =
    'DiagnosticReport?_include=DiagnosticReport:result&_include:iterate=Observation:performer&code=34133-9&based-on._id=' + entryId;
    const diagnosticReportAndRelatedResourcesEntry: fhirR4.BundleEntry = new fhirR4.BundleEntry();
    const diagnosticReportAndRelatedResourcesEntryRequest: fhirR4.BundleRequest = new fhirR4.BundleRequest();
    diagnosticReportAndRelatedResourcesEntryRequest.method = 'GET';
    diagnosticReportAndRelatedResourcesEntryRequest.url = diagnosticReportAndRelatedResourcesQuery;
    diagnosticReportAndRelatedResourcesEntry.request = diagnosticReportAndRelatedResourcesEntryRequest;
    bundle.entry.push(diagnosticReportAndRelatedResourcesEntry);

    const procedureAndRelatedResourcesQuery =
    'Procedure?_include=Procedure:performer&_include=Procedure:report&_include:iterate=DiagnosticReport:result&based-on._id=' + entryId;
    const procedureAndRelatedResourcesEntry: fhirR4.BundleEntry = new fhirR4.BundleEntry();
    const procedureAndRelatedResourcesEntryRequest: fhirR4.BundleRequest = new fhirR4.BundleRequest();
    procedureAndRelatedResourcesEntryRequest.method = 'GET';
    procedureAndRelatedResourcesEntryRequest.url = procedureAndRelatedResourcesQuery;
    procedureAndRelatedResourcesEntry.request = procedureAndRelatedResourcesEntryRequest;
    bundle.entry.push(procedureAndRelatedResourcesEntry);

    return this.postBundleResource(bundle);
  }

  fetchAllEntryClinicalResources(entryId: string) {
    const queryParams = 'ServiceRequest' + entryId;
    return this.executeGETQueryWithParams(queryParams, true);
  }

  fetchEntryAppointments(entryId: string) {
    const queryParams = `Appointment?based-on=${entryId}`;
    return this.executeGETQueryWithParams(queryParams, true);
  }

  fetchEntryProcedures(entryId: string) {
    const queryParams = `Procedure?based-on=${entryId}&_include=Procedure:report`;
    return this.executeGETQueryWithParams(queryParams, true);
  }

  fetchDiagnosticReports(entryIds: string[]) {
    const ids = entryIds?.join(',');
    const queryParams = `DiagnosticReport?_id=${ids}&_include=DiagnosticReport:result`;
    return this.executeGETQueryWithParams(queryParams, true);
  }

  postBundleResource(bundle: any) {
    const headers = this.getBundleHeaders();
    return this.httpClient.post(environment.resourceProviderURI, bundle, { headers }).toPromise();
  }

  saveEntry(bundle: fhirR4.Bundle) {
    const headers = this.getHeaders();
    return this.httpClient.post(environment.saveEntryURI, bundle, { headers }).toPromise();
  }

  updateLaunchContext(data: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: 'Bearer ' + this.oauthService.getAccessToken()
    });
    const query = '/user-management/default_launch_contexts/change';
    return this.httpClient.put(environment.jsonAdminURI + query, data, {headers}).toPromise();
  }

  getSupportDocumentsInfo() {
    return this.httpClient.get('./assets/config/admin-files.yaml', {
      observe: 'body',
      responseType: 'text'
    }).pipe(
      map(yamlString => parse(yamlString))
    ).toPromise();
  }

}
